import { StatIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { unitById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Sheet } from "../base/panels/shared.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { portraitUrl } from "../assets/manifest.js";
import { Stars } from "../units/shared.js";
import { useSession } from "../state/session.js";
import { chatApi, profilesApi, socialApi } from "./api.js";
import { jumpToMap } from "./ChatView.js";
import { lastSeenText, useSocial } from "./state.js";
import { CosmeticFrame } from "../store/cosmeticPresentation.js";
import { GmBadge } from "./GmBadge.js";

/** Profile card for any player, with the cosmetics editor for the own profile (R73). */
export function ProfileSheet() {
  const t = useT();
  const profile = useSocial((s) => s.profile);
  const options = useSocial((s) => s.profileOptions);
  const close = useSocial((s) => s.closeProfile);
  const openProfile = useSocial((s) => s.openProfile);
  const refreshOptions = useSocial((s) => s.refreshProfileOptions);
  const openDm = useSocial((s) => s.openDm);
  const run = useSocial((s) => s.run);
  const me = useSession((s) => s.account);
  const [edit, setEdit] = useState(false);
  useEffect(() => {
    if (profile?.relation.self && edit && !options) void refreshOptions();
  }, [profile, edit, options, refreshOptions]);
  if (!profile) return null;
  const p = profile;
  const self = p.relation.self;
  const update = async (patch: Parameters<typeof profilesApi.update>[0]) => {
    const card = await run(() => profilesApi.update(patch), t("settings.saved"));
    if (card) {
      useSocial.setState({ profile: card });
      void refreshOptions();
    }
  };
  const message = async () => {
    if (!me) return;
    await openDm(p.accountId, p.name);
    close();
    navigate("/app/chat");
  };
  return (
    <Sheet title={<span style={{ color: p.colour }}>{p.name}</span>} onClose={close} testId="profile-sheet" actions={<span className="badge" style={{ borderColor: p.frameColour, color: p.frameColour }}>{pick(p.titleName)}</span>}>
      <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
        <CosmeticFrame id={p.frame} colour={p.frameColour} avatar={p.avatar} />
        <div className="grow">
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <span className={`presence-dot ${p.online ? "on" : ""}`} />
            <span className="hint" data-testid="profile-lastseen">{lastSeenText(p.lastSeen)}</span>
            {p.vip.megaRank > 0 ? <span className="badge gold">MegaVIP {p.vip.megaRank}</span> : p.vip.level > 0 ? <span className="badge gold">VIP {p.vip.level}</span> : null}
            {p.role === "bot" ? <span className="badge">NPC</span> : null}
            <GmBadge role={p.role} />
            {p.alliance ? <span className="badge" data-testid="profile-alliance">[{p.alliance.tag}] {p.alliance.name}</span> : null}
          </div>
          <div className="stats" style={{ marginTop: 8 }}>
            <div className="stat"><span className="stat-label">HQ</span>{p.hq}</div>
            <div className="stat"><span className="stat-label"><StatIcon stat="power" /></span>{formatCompact(p.power)}</div>
            <div className="stat"><span className="stat-label">{t("social.profile.kills")}</span>{p.kills}</div>
            <div className="stat"><span className="stat-label">{t("social.profile.raids")}</span>{p.raidsWon}</div>
            <div className="stat"><span className="stat-label">{t("social.profile.defences")}</span>{p.defences}</div>
            <div className="stat"><span className="stat-label">{t("social.profile.arena")}</span>#{p.arena.rank} · {p.arena.rating}</div>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            {t("social.profile.founded", { date: new Date(p.foundedAt).toLocaleDateString() })}
            {p.position ? (
              <>
                {" · "}
                <button type="button" className="plain mono" onClick={() => { jumpToMap(p.position!.x, p.position!.y); close(); }} data-testid="profile-coords">
                  <FunctionalIcon name="location" /> {p.position.x},{p.position.y}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {p.showcase.length ? (
        <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: "wrap" }} data-testid="profile-showcase">
          {p.showcase.map((u) => (
            <span key={u.slot} className="showcase-unit" title={pick(unitById(u.unitId).name)}>
              <img src={portraitUrl(u.unitId)} alt="" />
              <span className="hint">
                {u.level} <Stars stars={u.stars} size={8} />
              </span>
            </span>
          ))}
        </div>
      ) : null}
      {!self ? (
        <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          <Button style={{ minHeight: 32 }} onClick={() => { close(); navigate(`/app/visit/${p.accountId}`); }} data-testid="profile-visit"><FunctionalIcon name="home" /> {t("alliance.visit")}</Button>
          <Button variant="primary" style={{ minHeight: 32 }} disabled={p.relation.blockedMe} onClick={() => void message()} data-testid="profile-message">
            <FunctionalIcon name="mail" /> {t("social.profile.message")}
          </Button>
          {p.relation.neighbour || p.relation.contact ? (
            <Button style={{ minHeight: 32 }} onClick={() => void run(() => socialApi.assist(p.accountId), t("social.neighbours.assisted"))} data-testid="profile-assist">
              <FunctionalIcon name="people" /> {t("social.profile.assist")}
            </Button>
          ) : null}
          {p.position ? (
            <Button style={{ minHeight: 32 }} onClick={() => { jumpToMap(p.position!.x, p.position!.y); close(); }} data-testid="profile-attack">
              <FunctionalIcon name="attack" /> {t("social.profile.attack")}
            </Button>
          ) : null}
          <Button style={{ minHeight: 32 }} onClick={() => void run(() => (p.relation.contact ? socialApi.removeContact(p.accountId) : socialApi.addContact(p.accountId))).then(() => openProfile(p.accountId))} data-testid="profile-contact">
            <FunctionalIcon name={p.relation.contact ? "check" : "plus"} /> {t(p.relation.contact ? "social.profile.contactRemove" : "social.profile.contactAdd")}
          </Button>
          <Button variant={p.relation.blocked ? "default" : "danger"} style={{ minHeight: 32 }} onClick={() => void run(() => (p.relation.blocked ? chatApi.unblock(p.accountId) : chatApi.block(p.accountId))).then(() => openProfile(p.accountId))} data-testid="profile-block">
            <FunctionalIcon name={p.relation.blocked ? "unlock" : "block"} /> {t(p.relation.blocked ? "social.profile.unblock" : "social.profile.block")}
          </Button>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <Button style={{ minHeight: 32 }} onClick={() => setEdit(!edit)} data-testid="profile-edit">
            <FunctionalIcon name="edit" /> {t("social.profile.edit")}
          </Button>
          {edit && options ? (
            <div className="section" style={{ marginTop: 10 }} data-testid="profile-editor">
              <div className="hint">{t("social.profile.avatar")}</div>
              <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                {options.avatars.map((id) => (
                  <button key={id} type="button" className={`plain avatar-pick ${options.current.avatar === id ? "active" : ""}`} onClick={() => void update({ avatar: id })} title={pick(unitById(id).name)} data-testid={`avatar-${id}`}>
                    <img src={portraitUrl(id)} alt="" />
                  </button>
                ))}
              </div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <label className="field" style={{ minWidth: 140 }}>
                  {t("social.profile.frame")}
                  <select value={options.current.frame} onChange={(e) => void update({ frame: e.target.value })} data-testid="frame-select">
                    {options.frames.map((f) => (
                      <option key={f.id} value={f.id} disabled={!f.unlocked}>
                        {pick(f.name)}
                        {f.unlocked ? "" : ` (VIP ${f.vip})`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field" style={{ minWidth: 140 }}>
                  {t("social.profile.title")}
                  <select value={options.current.title} onChange={(e) => void update({ title: e.target.value })} data-testid="title-select">
                    {options.titles.map((x) => (
                      <option key={x.id} value={x.id} disabled={!x.unlocked}>
                        {pick(x.name)}
                        {x.unlocked ? "" : ` (${t("social.profile.locked")})`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field" style={{ minWidth: 140 }}>
                  {t("social.profile.colour")}
                  <select value={options.current.colour} onChange={(e) => void update({ colour: e.target.value })} data-testid="colour-select">
                    {options.colours.map((c) => (
                      <option key={c.id} value={c.id} disabled={!c.unlocked}>
                        {c.id}
                        {c.unlocked ? "" : ` (${t("social.profile.comms", { n: c.unlock })})`}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field" style={{ minWidth: 140 }}>
                  {t("social.profile.showcase")}
                  <select value={options.current.showcaseSlot} onChange={(e) => void update({ showcaseSlot: Number(e.target.value) })} data-testid="showcase-select">
                    {options.squads.map((s) => (
                      <option key={s.slot} value={s.slot}>
                        {s.name || t("world.squad", { n: s.slot })}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}
